/**
 * useModerationWorkQueueSetup hook
 *
 * Orchestrator hook that wires together all sub-hooks and derived state
 * needed by ModerationWorkQueuePage. Keeps the page component thin enough
 * to satisfy max-statements (≤ 20) and complexity (≤ 10) limits.
 */

import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Blocker } from 'react-router-dom';

import type { ModerationCase } from '../domain/ModerationCase';
import type { CombinedLabel } from '../domain/ModerationLabels';
import type { CaseCardState } from '../domain/ModerationSession';
import type { PreviewOptions } from '../domain/PreviewOptions';
import { scrollCardIntoView } from '../utils/scrollCardIntoView';

import { useAssignModerationCase } from './useAssignModerationCase';
import { useCardGridNavigation } from './useCardGridNavigation';
import { useClassificationMenu } from './useClassificationMenu';
import type { ClassificationMenuState } from './useClassificationMenu';
import { useCombinedLabels } from './useCombinedLabels';
import type { UseModerationCountdownReturn } from './useModerationCountdown';
import { useModerationSession } from './useModerationSession';
import { useModerationSubmit } from './useModerationSubmit';
import { useModerationTimerGuard } from './useModerationTimerGuard';
import { usePreviewOptions } from './usePreviewOptions';
import { useStopModeration } from './useStopModeration';

/** All data and handlers the ModerationWorkQueuePage needs to render */
export interface ModerationWorkQueueSetup {
  /** Whether the initial assign request is loading */
  loading: boolean;
  /** Assignment errors (shown as full-page error state) */
  errors: DomainError[] | null;
  /** True when no cases were returned from the assign API */
  isEmpty: boolean;
  /** True when all session cases have been processed */
  isCompleted: boolean;
  /** Total completed cases (for completed state) */
  completedCount: number;
  /** Navigate back to the moderation dashboard */
  handleReturnToDashboard: () => void;

  /* ---------- Inline error banners ---------- */
  submitErrors: DomainError[] | null;

  /* ---------- Loading flags ---------- */
  submitLoading: boolean;

  /* ---------- Action handlers ---------- */
  handleStopModeration: () => void;
  handleSubmit: () => Promise<void>;
  handleSubmitAndStop: () => Promise<void>;
  handleApplyLabels: (labelIds: string[], feedbackComment?: string) => void;

  /* ---------- Card grid ---------- */
  handleCardGridKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  cardGridRef: React.RefObject<HTMLDivElement | null>;
  sessionCases: ModerationCase[];
  totalCases: number;
  cardStates: Record<string, CaseCardState>;
  focusedCaseId: string | null;
  focusCase: (caseId: string) => void;
  undoLabels: (caseId: string) => void;
  /** Combined label list for resolving `"labelId:severity"` display names */
  communityLabels: CombinedLabel[];

  /* ---------- Stats ---------- */
  pendingCount: number;
  appliedCount: number;
  isSubmitDisabled: boolean;

  /* ---------- Labels panel ---------- */
  topicName: string;
  focusedCase: ModerationCase | null;
  communityId: string;
  selectedLabels: string[];
  setSelectedLabels: (labels: string[]) => void;
  comment: string;
  setComment: (comment: string) => void;

  /* ---------- Action menu / Classification dialog ---------- */
  classificationMenu: ClassificationMenuState;

  /* ---------- Preview options ---------- */
  /** Current preview effect toggle states */
  previewOptions: PreviewOptions;
  /** Toggle a single preview effect by key */
  togglePreview: (key: keyof PreviewOptions) => void;

  /* ---------- Navigation guard ---------- */
  /** React Router blocker for unsaved-work navigation guard */
  blocker: Blocker;

  /* ---------- Countdown timer ---------- */
  /** Session TTL countdown timer state */
  countdown: UseModerationCountdownReturn;
}

/** Convert URL-formatted topic ID (e.g. "chat") to display name ("Chat") */
function formatTopicName(topicId: string | undefined): string {
  if (!topicId) return '';

  return topicId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Orchestrator hook for the Moderation Work Queue page.
 * Wires sub-hooks together and returns all data the page JSX needs.
 */
export function useModerationWorkQueueSetup(): ModerationWorkQueueSetup {
  const { previewOptions, togglePreview } = usePreviewOptions();
  const { communityId = '', topicId } = useParams<{
    communityId: string;
    topicId: string;
  }>();
  const navigate = useNavigate();
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const { assignCases, cases, loading, errors } = useAssignModerationCase();
  const { labels: allCommunityLabels } = useCombinedLabels(communityId);
  const { state: sessionState, focusCase, applyLabels, undoLabels, appliedCaseIds, loadNextCases, stopSession, submitCases } = useModerationSession();
  const topicName = useMemo(() => formatTopicName(topicId), [topicId]);
  const handleReturnToDashboard = useCallback(() => navigate(`/communities/${communityId}/moderation`), [navigate, communityId]);

  const { handleSubmit, handleSubmitAndStop, submitLoading, submitErrors, casesLoadedRef } = useModerationSubmit({
    appliedCaseIds,
    sessionCases: sessionState.cases,
    cardStates: sessionState.cardStates,
    submitCases,
    communityId,
    topicName,
    assignCases,
    onReturnToDashboard: handleReturnToDashboard,
  });

  const { handleStopModeration } = useStopModeration({
    sessionCases: sessionState.cases,
    appliedCaseIds,
    stopSession,
    onReturnToDashboard: handleReturnToDashboard,
  });

  const { handleCardGridKeyDown, cardGridRef } = useCardGridNavigation({
    sessionCases: sessionState.cases,
    focusedCaseId: sessionState.focusedCaseId,
    focusCase,
  });

  // Classification lookup (action menu -> dialog)
  const classificationMenu = useClassificationMenu({ communityId, sessionCases: sessionState.cases, focusedCaseId: sessionState.focusedCaseId });

  // Session TTL countdown, navigation guard, and stats cache warming (extracted sub-hook)
  const { countdown, blocker } = useModerationTimerGuard({
    communityId,
    topicName,
    sessionCases: sessionState.cases,
    isActive: sessionState.isActive,
  });

  // Fetch cases on mount
  useEffect(() => {
    if (communityId && topicName) {
      void assignCases({ variables: { communityId, topicName } });
    }
  }, [communityId, topicName, assignCases]);

  // Load cases into session when they arrive from the assign API
  useEffect(() => {
    if (cases.length > 0 && !casesLoadedRef.current) {
      casesLoadedRef.current = true;
      loadNextCases(cases);
    }
  }, [cases, loadNextCases, casesLoadedRef]);

  // Reset label selection when focused case changes
  useEffect(() => {
    setSelectedLabels([]);
    setComment('');
  }, [sessionState.focusedCaseId]);

  const handleApplyLabels = useCallback(
    (labelIds: string[], feedbackComment?: string) => {
      const { focusedCaseId: focusedId, cases, cardStates } = sessionState;

      if (!focusedId) return;

      applyLabels(focusedId, labelIds, feedbackComment);
      setSelectedLabels([]);
      setComment('');
      track('moderation_labels_applied', {
        caseId: focusedId,
        labelCount: String(labelIds.length),
      });

      // Auto-advance to next pending case after applying labels
      const currentIndex = cases.findIndex(c => c.caseId === focusedId);
      const nextPending = cases.find((c, i) => i > currentIndex && cardStates[c.caseId]?.status === 'pending');

      if (nextPending) {
        focusCase(nextPending.caseId);

        // Scroll the next card into view after React renders
        requestAnimationFrame(() => {
          const cardItems = cardGridRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
          const nextIndex = cases.findIndex(c => c.caseId === nextPending.caseId);
          const scrollContainer = cardGridRef.current?.parentElement;
          const targetCard = cardItems?.[nextIndex];

          if (scrollContainer && targetCard) {
            scrollCardIntoView(scrollContainer, targetCard);
          }
        });
      }
    },
    [applyLabels, focusCase, cardGridRef, sessionState]
  );

  // Inline derived values directly in the return to stay within max-statements
  return {
    loading,
    errors,
    pendingCount: sessionState.cases.length - appliedCaseIds.length,
    focusedCase: sessionState.cases.find(c => c.caseId === sessionState.focusedCaseId) ?? null,
    sessionCases: sessionState.cases,
    totalCases: sessionState.cases.length,
    appliedCount: appliedCaseIds.length,
    isSubmitDisabled: appliedCaseIds.length < sessionState.cases.length || submitLoading,
    isEmpty: cases.length === 0 && !loading,
    isCompleted: sessionState.cases.length === 0 && sessionState.completedCount > 0,
    communityLabels: allCommunityLabels,
    completedCount: sessionState.completedCount,
    handleReturnToDashboard,
    submitErrors,
    submitLoading,
    handleStopModeration,
    handleSubmit,
    handleSubmitAndStop,
    handleApplyLabels,
    handleCardGridKeyDown,
    cardGridRef,
    cardStates: sessionState.cardStates,
    focusedCaseId: sessionState.focusedCaseId,
    focusCase,
    undoLabels,
    topicName,
    communityId,
    selectedLabels,
    setSelectedLabels,
    comment,
    setComment,
    classificationMenu,
    previewOptions,
    togglePreview,
    blocker,
    countdown,
  };
}
